import partition from 'lodash.partition';
import { match, P } from 'ts-pattern';
import {
  ApiMasterDataQueryResponseDataPoint,
  ApiMetricQueryResponse,
} from '../../../../common/api/api-interfaces';
import { QueryToSqlHelper } from '../../../../common/api/query-to-sql-helper';
import { TimeSliderConfig } from '../../../../common/filter/filter-store';
import { getDatesInRange } from '../../../../common/filter/utils';
import {
  CohortMetricId,
  EmployeeCohortMetricId,
  MetricGroupId,
  RegularMetricId,
} from '../../../../common/graphql/generated/graphql-sdk';
import { getModifiedInputsAndFiltersForBenchmark } from '../../../../common/services/BenchmarkWrapperService';
import { uuid } from '../../../../common/utilFunctions/utils';
import {
  MetricId,
  TimeSelectionInput,
} from '../../../../common/v2/api/types-graphql';
import { metricIdNeedsEmptyCohortFilter } from '../../../../common/v2/api/utils';
import { Filter } from '../../../../common/v2/common/components/filter/filterbar/types';
import { MetricTypes } from '../../../../common/v2/common/types';
import {
  singleValueTimeSegment,
  timeSliderGranularityToTimeSelectionInput,
} from '../backend-metrics/utils';
import {
  BalanceSheetTableService,
  PbsTableRowData,
  PbsTableServiceResponseData,
  ResponseDataMetaData,
  ValueByDate,
} from '../types';
import {
  allowedFiltersForFinancialMetrics,
  executeSqlQuery,
  formatPeriodConfigDate,
  getFormattedPbsTableRowData,
  getGranularityConfig,
  getHeaderObjs,
  getTimeSlicesAccToTimeSliderConfig,
} from '../utils';

const toCohortMetricIdTypes = (
  cohortMetricId: CohortMetricId[]
): { type: 'CohortMetricId'; value: CohortMetricId[] } => ({
  type: 'CohortMetricId',
  value: cohortMetricId,
});

const toEmployeeCohortMetricIdTypes = (
  employeeCohortMetricId: EmployeeCohortMetricId[]
): { type: 'EmployeeCohortMetricId'; value: EmployeeCohortMetricId[] } => ({
  type: 'EmployeeCohortMetricId',
  value: employeeCohortMetricId,
});

const toRegularMetricIdTypes = (
  regularMetricId: RegularMetricId[]
): { type: 'RegularMetricId'; value: RegularMetricId[] } => ({
  type: 'RegularMetricId',
  value: regularMetricId,
});

export const PbsTableService: BalanceSheetTableService = async (
  inputs,
  filters,
  latestVersions,
  legacyStores,
  legacyDomainScopedStores
) => {
  const { metricStore, cachedMasterDataApiService, timeSliderStore } =
    legacyDomainScopedStores;
  const { timeSliderConfig, selectedPbsValues, selectedBenchmark } = inputs;
  const selectedMetricGroupIds = selectedPbsValues.flatMap((v) => v.subItems);
  const [
    selectedRegularMetricGroupIds,
    selectedCohortMetricGroupIds,
    selectedEmployeeCohortMetricGroupIds,
  ] = selectedMetricGroupIds.reduce<
    [MetricGroupId[], MetricGroupId[], MetricGroupId[]]
  >(
    (acc, m) => {
      if (metricStore.isMetricGroupRegular(m)) {
        acc[0].push(m);
      } else if (metricStore.isMetricGroupCohort(m)) {
        acc[1].push(m);
      } else if (metricStore.isMetricGroupEmployeeCohort(m)) {
        acc[2].push(m);
      }
      return acc;
    },
    [[], [], []]
  );
  const selectedRegularMetricDetails = selectedRegularMetricGroupIds.flatMap(
    (m) => metricStore.getAllowedSortedMetricIdsForMetricGroup(m)
  );
  const selectedCohortMetricDetails = selectedCohortMetricGroupIds.flatMap(
    (m) => metricStore.getAllowedSortedMetricIdsForMetricGroup(m)
  );
  const selectedEmployeeCohortMetricDetails =
    selectedEmployeeCohortMetricGroupIds.flatMap((m) =>
      metricStore.getAllowedSortedMetricIdsForMetricGroup(m)
    );

  //TODO: this is temporary until we migrate financialmetrics to cohort table
  const [financialMetricDetails, otherMetricDetails] = partition(
    selectedRegularMetricDetails,
    (metric) => metricStore.isMetricFinancial(metric)
  );

  // TODO: Fix type conversion
  const financialMetricIds = financialMetricDetails.map(
    (m) => m.id
  ) as unknown as RegularMetricId[];
  const otherMetricIds = otherMetricDetails.map(
    (m) => m.id
  ) as unknown as RegularMetricId[];
  const selectedCohortMetricIds = selectedCohortMetricDetails.map(
    (m) => m.id
  ) as unknown as CohortMetricId[];
  const selectedEmployeeCohortMetricIds =
    selectedEmployeeCohortMetricDetails.map(
      (m) => m.id
    ) as unknown as EmployeeCohortMetricId[];

  const financialMetricsFilters: Filter[] = allowedFiltersForFinancialMetrics(
    filters as Filter[]
  );

  let financialMetricsRegularBackendBenchmarkPromise:
    | (() => Promise<{ dataPoints: ApiMasterDataQueryResponseDataPoint[] }>)
    | null = null;
  let regularBackendBenchmarkPromise:
    | (() => Promise<{ dataPoints: ApiMasterDataQueryResponseDataPoint[] }>)
    | null = null;
  let employeeCohortBackendBenchmarkPromise:
    | (() => Promise<{ dataPoints: ApiMasterDataQueryResponseDataPoint[] }>)
    | null = null;
  let cohortBackendBenchmarkPromise:
    | (() => Promise<{ dataPoints: ApiMasterDataQueryResponseDataPoint[] }>)
    | null = null;
  let benchmarkDatesInRange: string[] = [];
  const getBackendPromise = (
    metricIds:
      | { type: 'CohortMetricId'; value: CohortMetricId[] }
      | { type: 'EmployeeCohortMetricId'; value: EmployeeCohortMetricId[] }
      | { type: 'RegularMetricId'; value: RegularMetricId[] },
    filters: Filter[],
    timeSliderConfig: TimeSliderConfig,
    timeStrategy: 'periodTotal' | 'overTime'
  ) => {
    return () => {
      const timeSelection: TimeSelectionInput = match(timeStrategy)
        .with('periodTotal', () => singleValueTimeSegment(timeSliderConfig))
        .with('overTime', () =>
          timeSliderGranularityToTimeSelectionInput(timeSliderConfig)
        )
        .exhaustive();
      return metricIds.value.length > 0
        ? match(metricIds)
            .with(
              { type: 'CohortMetricId', value: P.select() },
              (cohortMetricIds: CohortMetricId[]) => {
                // Assumes that if the first metricId needs empty cohort filter, then all metricIds need empty cohort filter
                const needsEmptyCohortFilter = metricIdNeedsEmptyCohortFilter(
                  cohortMetricIds[0] as unknown as MetricId
                );
                const sqlFilters =
                  QueryToSqlHelper.getFilterConditions(
                    filters,
                    MetricTypes.COHORT,
                    needsEmptyCohortFilter
                  ) ?? undefined;
                return cachedMasterDataApiService.queryCohortMetrics(
                  timeSelection,
                  cohortMetricIds,
                  sqlFilters
                );
              }
            )
            .with(
              { type: 'EmployeeCohortMetricId', value: P.select() },
              (employeeCohortMetricIds: EmployeeCohortMetricId[]) => {
                // Assumes that if the first metricId needs empty cohort filter, then all metricIds need empty cohort filter
                const needsEmptyCohortFilter = metricIdNeedsEmptyCohortFilter(
                  employeeCohortMetricIds[0] as unknown as MetricId
                );
                const sqlFilters =
                  QueryToSqlHelper.getFilterConditions(
                    filters,
                    MetricTypes.EMPLOYEE_COHORT,
                    needsEmptyCohortFilter
                  ) ?? undefined;
                return cachedMasterDataApiService.queryEmployeeCohortMetrics(
                  timeSelection,
                  employeeCohortMetricIds,
                  sqlFilters
                );
              }
            )
            .with(
              { type: 'RegularMetricId', value: P.select() },
              (regularMetricIds: RegularMetricId[]) => {
                const sqlFilters =
                  QueryToSqlHelper.getFilterConditions(
                    filters,
                    MetricTypes.REGULAR,
                    false
                  ) ?? undefined;
                return cachedMasterDataApiService.queryMetrics(
                  timeSelection,
                  regularMetricIds,
                  sqlFilters
                );
              }
            )
            .otherwise(() => {
              throw new Error('Unexpected metric id array');
            })
        : Promise.resolve<ApiMetricQueryResponse>({ dataPoints: [] });
    };
  };
  if (selectedBenchmark) {
    const { inputs: benchmarkInputs, filters: benchmarkFilters } =
      getModifiedInputsAndFiltersForBenchmark(
        inputs,
        filters,
        [],
        timeSliderConfig,
        selectedBenchmark
      );
    const { filters: financialMetricsBenchmarkFilters } =
      getModifiedInputsAndFiltersForBenchmark(
        inputs,
        financialMetricsFilters,
        [],
        timeSliderConfig,
        selectedBenchmark
      );
    const benchmarkTimeSliderConfig = benchmarkInputs.timeSliderConfig;
    benchmarkDatesInRange = getDatesInRange(benchmarkTimeSliderConfig);

    financialMetricsRegularBackendBenchmarkPromise = getBackendPromise(
      toRegularMetricIdTypes(financialMetricIds),
      financialMetricsBenchmarkFilters as Filter[],
      benchmarkTimeSliderConfig,
      'periodTotal'
    );

    regularBackendBenchmarkPromise = getBackendPromise(
      toRegularMetricIdTypes(otherMetricIds),
      benchmarkFilters as Filter[],
      benchmarkTimeSliderConfig,
      'periodTotal'
    );

    employeeCohortBackendBenchmarkPromise = getBackendPromise(
      toEmployeeCohortMetricIdTypes(selectedEmployeeCohortMetricIds),
      benchmarkFilters as Filter[],
      benchmarkTimeSliderConfig,
      'periodTotal'
    );

    cohortBackendBenchmarkPromise = getBackendPromise(
      toCohortMetricIdTypes(selectedCohortMetricIds),
      benchmarkFilters as Filter[],
      benchmarkTimeSliderConfig,
      'periodTotal'
    );
  }

  const financialMetricsRegularBackendOverTimePromise = getBackendPromise(
    toRegularMetricIdTypes(financialMetricIds),
    financialMetricsFilters,
    timeSliderConfig,
    'overTime'
  );

  const regularBackendOverTimePromise = getBackendPromise(
    toRegularMetricIdTypes(otherMetricIds),
    filters as Filter[],
    timeSliderConfig,
    'overTime'
  );

  const employeeCohortBackendOverTimePromise = getBackendPromise(
    toEmployeeCohortMetricIdTypes(selectedEmployeeCohortMetricIds),
    filters as Filter[],
    timeSliderConfig,
    'overTime'
  );

  const cohortBackendOverTimePromise = getBackendPromise(
    toCohortMetricIdTypes(selectedCohortMetricIds),
    filters as Filter[],
    timeSliderConfig,
    'overTime'
  );

  const financialMetricsRegularBackendTotalPeriodPromise = getBackendPromise(
    toRegularMetricIdTypes(financialMetricIds),
    financialMetricsFilters,
    timeSliderConfig,
    'periodTotal'
  );

  const regularBackendTotalPeriodPromise = getBackendPromise(
    toRegularMetricIdTypes(otherMetricIds),
    filters as Filter[],
    timeSliderConfig,
    'periodTotal'
  );

  const employeeCohortBackendTotalPeriodPromise = getBackendPromise(
    toEmployeeCohortMetricIdTypes(selectedEmployeeCohortMetricIds),
    filters as Filter[],
    timeSliderConfig,
    'periodTotal'
  );

  const cohortBackendTotalPeriodPromise = getBackendPromise(
    toCohortMetricIdTypes(selectedCohortMetricIds),
    filters as Filter[],
    timeSliderConfig,
    'periodTotal'
  );

  const executeFinancialMetricsRegularBackendSqlQuery = executeSqlQuery(
    financialMetricDetails,
    selectedBenchmark,
    financialMetricsRegularBackendOverTimePromise,
    financialMetricsRegularBackendTotalPeriodPromise,
    financialMetricsRegularBackendBenchmarkPromise
  );
  const executeRegularBackendSqlQuery = executeSqlQuery(
    otherMetricDetails,
    selectedBenchmark,
    regularBackendOverTimePromise,
    regularBackendTotalPeriodPromise,
    regularBackendBenchmarkPromise
  );
  const executeEmployeeCohortBackendSqlQuery = executeSqlQuery(
    selectedEmployeeCohortMetricDetails,
    selectedBenchmark,
    employeeCohortBackendOverTimePromise,
    employeeCohortBackendTotalPeriodPromise,
    employeeCohortBackendBenchmarkPromise
  );
  const executeCohortBackendSqlQuery = executeSqlQuery(
    selectedCohortMetricDetails,
    selectedBenchmark,
    cohortBackendOverTimePromise,
    cohortBackendTotalPeriodPromise,
    cohortBackendBenchmarkPromise
  );

  const timeSlices = getTimeSlicesAccToTimeSliderConfig(timeSliderConfig);

  let meta: ResponseDataMetaData = {};
  const tableData: PbsTableServiceResponseData = {
    rowGroups: [
      ...(await Promise.all(
        selectedPbsValues.map(async ({ value: rowGroup, subItems }) => {
          const [
            regularResponseData,
            employeeCohortResponseData,
            cohortResponseData,
            financialMetricsResponseData,
          ] = await Promise.all([
            executeRegularBackendSqlQuery,
            executeEmployeeCohortBackendSqlQuery,
            executeCohortBackendSqlQuery,
            executeFinancialMetricsRegularBackendSqlQuery,
          ]);
          meta = {
            ...regularResponseData.meta,
            ...employeeCohortResponseData.meta,
            ...cohortResponseData.meta,
            ...financialMetricsResponseData.meta,
          };
          return {
            metricCategory: rowGroup,
            rows: [
              ...(await Promise.all(
                subItems.map(async (item) => {
                  const isCohort = metricStore.isMetricGroupCohort(item);
                  const isEmployeeCohort =
                    metricStore.isMetricGroupEmployeeCohort(item);
                  const isFinancial = metricStore.isMetricGroupFinancial(item);

                  let response;
                  if (isEmployeeCohort) {
                    response = employeeCohortResponseData;
                  } else if (isCohort) {
                    response = cohortResponseData;
                  } else {
                    // regular metric
                    response = isFinancial
                      ? financialMetricsResponseData
                      : regularResponseData;
                  }

                  const values = response.values[item] as ValueByDate;
                  return Promise.resolve(
                    getFormattedPbsTableRowData(
                      item,
                      values,
                      timeSlices,
                      formatPeriodConfigDate(
                        getGranularityConfig(timeSliderConfig)
                      ),
                      selectedBenchmark
                    ) as PbsTableRowData<MetricGroupId>
                  );
                })
              )),
            ],
          };
        })
      )),
    ],
  } as PbsTableServiceResponseData; // TODO: Need to remove this assertion later

  const { fixedHeaderObjs, timeHeaderObjs, benchmarkHeaderObjs } =
    getHeaderObjs(timeSliderConfig, selectedBenchmark, timeSliderStore);
  const response = {
    fixedHeaderObjs,
    timeHeaderObjs,
    benchmarkHeaderObjs,
    tableData,
    meta,
  };
  return response;
};

PbsTableService.cacheKey = uuid();
